import {
  Agency,
  CoverHolder,
  Date as DateStruct,
  Decimal,
  PolicyState,
  Address,
  Quote,
  Money,
  ApplicationFormResponse,
  ApplicationFormData,
  OverridableBool,
  OverridableDate,
  OverridableDecimal,
  OverridableNumber,
  OverridableString,
  OverridableYesNoUnknown,
} from '@policyfly/protobuf'
import { Struct, Timestamp, Value } from '@policyfly/protobuf/google/protobuf'
import {
  Address as ScheduleAddress,
  Date as DateScheduleStruct,
  ScheduleItem,
} from '@policyfly/protobuf/pfschedule'
import {
  dateToString,
  decimalToString,
  timestampToString,
  agencyOrCoverHolderToString,
  stringToDate,
  stringToDecimal,
  stringToTimestamp,
  stringToAgencyOrCoverHolder,
  moneyToString,
  stringToMoney,
  getOneofKindValue,
} from '@policyfly/utils/protobuf'
import { isObject } from '@policyfly/utils/type'
import { normalizeFieldInfo, RepeatType, ScalarType } from '@protobuf-ts/runtime'
import { isReadonly, reactive, toRaw } from 'vue'

import { WORKING_COPIES_KEY } from '@/constants'
import { devtools } from '@/plugins/devtools/api'
import { booleanToYesNoUnknown, convertScalar } from '@/utils/protobuf'

import type { ReadonlyPolicyState } from '@/stores/protobuf'
import type { AnyObject, TSFixMe } from '@policyfly/types/common'
import type { FieldInfo, IMessageType, JsonValue, UnknownOneofGroup } from '@protobuf-ts/runtime'

/**
 * A key that points to a unique area in a PolicyState where untyped temporary data can be written.
 *
 * This is useful for storing some temporary values that do not need to be persisted.
 */
export const SCRATCH_KEY = '$scratch'

/**
 * The key in an `ApplicationFormData` that `BUCKET_APPLICATION` paths are relative to.
 */
export const APPLICATION_RESPONSES_KEY = 'responses'

/**
 * A key that points to the default item structure of an `ApplicationFormList`.
 *
 * This is seeded when form data is created and used to create new items of the
 * correct shape when drilling.
 */
export const FORM_LIST_DEFAULT_ITEM_KEY = Symbol('formListDefaultItem')

/**
 * Returns true if the passed {@link FieldInfo} is an array-type field.
 */
export function isArrayFieldInfo (fieldInfo: Partial<Pick<FieldInfo, 'repeat'>>): boolean {
  return fieldInfo.repeat === RepeatType.PACKED || fieldInfo.repeat === RepeatType.UNPACKED
}

type FieldIndexEntry =
  | { kind: 'local', field: FieldInfo }
  | { kind: 'oneof' }

/**
 * Lazy per-message-type index of `localName` and `oneof` names to {@link FieldInfo} entries.
 * Replaces the linear scan over `message.fields` in {@link drillFieldInfo} with O(1) lookups.
 */
const messageFieldIndex = new WeakMap<IMessageType<object>, Map<string, FieldIndexEntry>>()

function getMessageFieldIndex (message: IMessageType<object>): Map<string, FieldIndexEntry> {
  let entry = messageFieldIndex.get(message)
  if (!entry) {
    entry = new Map<string, FieldIndexEntry>()
    for (const f of message.fields) {
      if (!entry.has(f.localName)) {
        entry.set(f.localName, { kind: 'local', field: f })
      }
      if (f.oneof && !entry.has(f.oneof)) {
        entry.set(f.oneof, { kind: 'oneof' })
      }
    }
    messageFieldIndex.set(message, entry)
  }
  return entry
}

/**
 * The {@link FieldInfo} for the base {@link PolicyState} message.
 */
export const PolicyStateFieldInfo = normalizeFieldInfo({
  no: 1,
  name: PolicyState.typeName,
  kind: 'message',
  T: () => PolicyState,
})

/**
 * The {@link FieldInfo} for the base {@link Quote} message.
 */
export const QuoteFieldInfo = normalizeFieldInfo({
  no: 1,
  name: Quote.typeName,
  kind: 'message',
  T: () => Quote,
})

/**
 * The {@link FieldInfo} for the base {@link Struct} message.
 */
export const StructFieldInfo = normalizeFieldInfo({
  no: 1,
  name: Struct.typeName,
  kind: 'message',
  T: () => Struct,
})

/**
 * The {@link FieldInfo} for the base {@link Value} message.
 */
export const ValueFieldInfo = normalizeFieldInfo({
  no: 1,
  name: Value.typeName,
  kind: 'message',
  T: () => Value,
})

/**
 * The {@link FieldInfo} for the base {@link ApplicationFormResponse} message.
 */
export const ApplicationFormResponseFieldInfo = normalizeFieldInfo({
  no: 1,
  name: ApplicationFormResponse.typeName,
  kind: 'message',
  T: () => ApplicationFormResponse,
})

/**
 * The {@link FieldInfo} for the base {@link ScheduleItem} message.
 */
export const ScheduleItemFieldInfo = normalizeFieldInfo({
  no: 1,
  name: ScheduleItem.typeName,
  kind: 'message',
  T: () => ScheduleItem,
})

/**
 * The {@link FieldInfo} for the base {@link ApplicationFormData} message.
 */
export const ApplicationFormDataFieldInfo = normalizeFieldInfo({
  no: 1,
  name: ApplicationFormData.typeName,
  kind: 'message',
  T: () => ApplicationFormData,
})

export interface DrilledFieldInfo {
  /**
   * The parent value of the key.
   * The value you want to read/write will be the `key` in this value.
   */
  value: AnyObject | AnyObject[]
  /**
   * The key to the field, can be accessed using value[key].
   */
  key: string
  /**
   * Field information about the field stored under value[key].
   */
  fieldInfo: FieldInfo
  /**
   * The `typeName` of the message that owns `value` (e.g. `Value.typeName`, `Struct.typeName`).
   * Empty string when `value` is an array, a map, or otherwise not a known protobuf message.
   *
   * This can be used for performance optimisations to avoid the use of `Message.is` which is
   * extremely slow even at a shallow depth.
   */
  parentTypeName: string
  /**
   * Includes various versions of the path used to access the field.
   */
  paths: {
    /**
     * The fully qualified path to this field, replacing special syntaxes such as `(type=)`.
     */
    resolved: string
    /**
     * The path as it would appear within diff data, using uuid selectors instead of index selectors.
     */
    diff: string
  }
  /**
   * Extra information used to resolve the path based on external factors.
   */
  meta: {
    /**
     * The path of the currently selected quote.
     */
    selectedQuote: string
    /**
     * The current default data item to be used.
     * Helpful for informing items within an array of their data structure.
     */
    defaultDataItem: ApplicationFormResponse | null
    /**
     * Whether array items selected using the syntax `coverages.(type=3)` should be created
     * when no item matches the selector.
     * Set automatically when drilling into scratch data, or can be passed in
     * to seed an entire shape from an empty value.
     *
     * Has no effect unless `create` is also `true`.
     */
    createMissingSelectors: boolean
  }
}

export const PROXIED_OBJECT_KEY = Symbol('proxiedObject')
type CachedObject<T extends object> = T & { [PROXIED_OBJECT_KEY]: undefined }
interface CacheInfo {
  cache: PolicyStateCache
  pathParts: string[]
}
function isObjectToProxy (v: unknown): v is object {
  return !!v && typeof v === 'object' && !(v instanceof Uint8Array) && !(PROXIED_OBJECT_KEY in v)
}
function proxyObject <T extends object> (obj: T, cacheInfo: CacheInfo): CachedObject<T> {
  for (const [k, v] of Object.entries(obj)) {
    const newCacheInfo: CacheInfo = { ...cacheInfo, pathParts: [...cacheInfo.pathParts, k] }
    if (isObjectToProxy(v)) {
      obj[k as keyof T] = proxyObject(v, newCacheInfo) as TSFixMe
    }
  }

  // these never change so compute them once
  const prefix = cacheInfo.pathParts.join('.')
  const isArray = Array.isArray(obj)
  const arrayWorkingCopiesPrefix = `${prefix}.${WORKING_COPIES_KEY}`

  return new Proxy(obj, {
    set (target, path, value, receiver) {
      if (typeof path !== 'string') return Reflect.set(target, path, value, receiver)

      const isObjectValue = isObjectToProxy(value)
      if (isObjectValue) {
        value = proxyObject(value, { ...cacheInfo, pathParts: [...cacheInfo.pathParts, path] })
      }
      // the deletes below can trigger another reactive drill before this finished and hit the stale object here
      // we set the value first so if that does happen it's hitting the new object
      const result = Reflect.set(target, path, value, receiver)

      // iterate the raw cache to avoid reactive reads being tracked which is very expensive,
      // deletes/sets still happen on the reactive cache to trigger reactivity
      const rawCache = toRaw(cacheInfo.cache)

      if (isArray && !path.startsWith(WORKING_COPIES_KEY)) {
        // clear all items in an array in case index has shifted
        for (const k in rawCache) {
          const resolved = rawCache[k]!.paths.resolved
          if (resolved.startsWith(prefix) && !resolved.startsWith(arrayWorkingCopiesPrefix)) {
            delete cacheInfo.cache[k]
          }
        }
      } else {
        const full = prefix ? `${prefix}.${path}` : path
        // if setting an object, clear the object's path cache and everything nested
        // otherwise only clear nested keys (accounts for setting an object to null)
        const objectCachePath = isObjectValue ? full : `${full}.`
        for (const k in rawCache) {
          if (rawCache[k]!.paths.resolved.startsWith(objectCachePath)) {
            delete cacheInfo.cache[k]
          }
        }
      }

      return result
    },
    has (target, path) {
      if (path === PROXIED_OBJECT_KEY) return true
      return Reflect.has(target, path)
    },
  }) as CachedObject<T>
}

export const CACHE_KEY = Symbol('cacheKey')
export type PolicyStateCache = Record<string, DrilledFieldInfo>
export type CachedPolicyState<T extends PolicyState | ReadonlyPolicyState> = T & { [CACHE_KEY]: PolicyStateCache }
/**
 * Returns a proxy for the provided {@link PolicyState} that has an internal cache.
 * - A `readonly` PolicyState will be proxied to add a cache on top of the existing readonly proxy
 * - A non-`readonly` PolicyState (`reactive` or not) will add a cache, deeply proxy the raw object track any changes that would invalidate the cache,
 *  make that resultant proxy reactive and return it
 *
 * Mainly for use with {@link cachedDrillFieldInfo}.
 */
export function cachedPolicyState<T extends PolicyState | ReadonlyPolicyState> (policyState: T): CachedPolicyState<T> {
  const cache: PolicyStateCache = reactive({})
  const wasReadonly = isReadonly(policyState)
  const objectToProxy = wasReadonly ? policyState : toRaw(policyState)
  const baseProxy = new Proxy(objectToProxy, {
    get (target, path, receiver) {
      if (path === CACHE_KEY) return cache
      return Reflect.get(target, path, receiver)
    },
    has (target, path) {
      if (path === CACHE_KEY) return true
      return Reflect.has(target, path)
    },
  }) as CachedPolicyState<T>
  if (wasReadonly) return baseProxy
  return reactive(proxyObject(baseProxy, { cache, pathParts: [] })) as CachedPolicyState<T>
}

type PossiblyCachedPolicyState = PolicyState | ReadonlyPolicyState | CachedPolicyState<PolicyState> | CachedPolicyState<ReadonlyPolicyState>
/**
 * If provided a proxied PolicyState will use the cache if possible when drilling field info.
 * Can also be passed a un-proxied PolicyState and the cache will be ignored.
 */
export function cachedDrillFieldInfo (
  create: boolean,
  path: string,
  policyState: PossiblyCachedPolicyState,
): DrilledFieldInfo | null {
  const cache = CACHE_KEY in policyState ? policyState[CACHE_KEY] : null
  if (cache && path in cache) {
    return cache[path]!
  }
  const result = drillFieldInfo(
    create,
    PolicyStateFieldInfo,
    path,
    policyState,
  )
  if (result && cache) {
    cache[path] = result
    if (result.paths.resolved !== path) cache[result.paths.resolved] = result
  }
  return result
}

/**
 * Assigns a newly created value to `target[key]` and returns it read back through `target`.
 * A reactive `target` hands back its proxy, so further writes while drilling trigger reactivity
 * rather than silently mutating the raw object.
 */
function assignCreated<T extends object, K extends keyof T> (target: T, key: K, created: T[K]): T[K] {
  target[key] = created
  return target[key]
}

/**
 * Checks whether a Value is trying to write an array when a non-array exists or vice versa.
 */
function isMismatchedArray (v: Value | null, nextKeyIsArrIndex: boolean, path: string): boolean {
  if (!v?.kind) return false
  if (v.kind.oneofKind === 'nullValue') return true
  if (nextKeyIsArrIndex && v.kind.oneofKind !== 'listValue') {
    devtools.configWarning({
      type: 'data',
      title: 'Struct Value Type Mismatch',
      description: 'Expecting an array value but found a non-array, existing value may be overwritten',
      context: JSON.stringify({ path, v }, null, 2),
    })
    return true
  }
  if (!nextKeyIsArrIndex && v.kind.oneofKind === 'listValue') {
    devtools.configWarning({
      type: 'data',
      title: 'Struct Value Type Mismatch',
      description: 'Expecting a non-array value but found an array, existing value may be overwritten',
      context: JSON.stringify({ path, v }, null, 2),
    })
    return true
  }
  return false
}

/**
 * Used to check for special paths, such as `(type=3)`.
 */
export const ARR_ITEM_SELECT_REGEX = /^\((.*)=(.*)\)$/

/**
 * Creates a new item for a repeated message field, with the selector `key` set to `value`.
 * This allows a path such as `premiums.(type=3).amount` to be drilled when no item matches the selector.
 *
 * Returns `null` if the selector is invalid.
 */
function createSelectorItem (message: IMessageType<object>, key: string, value: string): AnyObject | null {
  const field = getMessageFieldIndex(message).get(key)
  if (field?.kind !== 'local') return null
  switch (field.field.kind) {
    case 'enum': return message.create({ [key]: Number(value) })
    case 'scalar': return message.create({ [key]: convertScalar(value, field.field.T) })
  }
  return null
}

/**
 * Fills any holes a list-value write created when its index extended past the array's previous length.
 * Must be called immediately after writing to a {@link Value Value's} `listValue` array, since the protobuf
 * library does not serialize sparse arrays correctly.
 *
 * Indices before `prevLength` are assumed to already be filled — this function only touches the
 * range `[prevLength, values.length - 2]`, which is exactly the gap a write past `prevLength` opens up.
 */
export function fixSparseValueArray (value: Value, prevLength: number): void {
  if (value.kind.oneofKind !== 'listValue') return
  const values = value.kind.listValue.values
  for (let i = prevLength; i < values.length - 1; i++) {
    values[i] = Value.fromJson(null)
  }
}

/**
 * Starting with a valid protobuf struct value, will drill down to the specified path and return the {@link DrilledFieldInfo} for it.
 *
 * @param create Whether to create missing parent values while drilling, if `false` will return `null` as soon as a value does not exist
 * @param fieldInfo The field information of the starting value being passed in
 * @param path The dot notation path to drill to, you can select items within arrays using the syntax `premiums.(type=3).amount`
 * @param value The starting value
 * @param paths see {@link DrilledFieldInfo.paths paths}
 * @param meta see {@link DrilledFieldInfo.meta meta}
 */
export function drillFieldInfo (
  create: boolean,
  fieldInfo: FieldInfo,
  path: string,
  value: AnyObject | AnyObject[],
  paths: DrilledFieldInfo['paths'] = { resolved: '', diff: '' },
  meta: DrilledFieldInfo['meta'] = { selectedQuote: '', defaultDataItem: null, createMissingSelectors: false },
): DrilledFieldInfo | null {
  let [key, ...rest] = path.split('.')
  if (!key) return null
  if (fieldInfo.kind !== 'message' && fieldInfo.kind !== 'map') return null

  // find info for next field
  let nextFieldInfo: FieldInfo | null = null
  let message: IMessageType<object> | null = null

  let diffKey = key
  if (fieldInfo.kind === 'map') {
    if (fieldInfo.V.kind === 'message') {
      nextFieldInfo = normalizeFieldInfo({
        no: 1,
        name: fieldInfo.V.T().typeName,
        kind: 'message',
        T: fieldInfo.V.T,
      })
    }
  } else {
    message = fieldInfo.T()

    // check for incorrect Value
    if (message.typeName === Value.typeName && !(value as Value).kind?.oneofKind) {
      return null
    }
    // rewrite address keys
    if ([Address.typeName, ScheduleAddress.typeName].includes(message.typeName) && key === 'address') {
      diffKey = key = 'address1'
    }
    if (message.typeName === ApplicationFormResponse.typeName) {
      if (key === 'address') {
        const v = value as ApplicationFormResponse
        if (v.value.oneofKind === 'object' && v.value.object.fields.address1?.value.oneofKind === 'string') {
          diffKey = key = 'address1'
        }
      }
    }

    let listValueArray: Value[] | null = null
    if (
      message.typeName === Value.typeName &&
      Value.is(value, 1) &&
      value.kind.oneofKind === 'listValue'
    ) {
      listValueArray = value.kind.listValue.values
    }
    if (isArrayFieldInfo(fieldInfo) || listValueArray) {
      if (key === WORKING_COPIES_KEY) {
        nextFieldInfo = fieldInfo
      } else {
        nextFieldInfo = { ...fieldInfo, repeat: RepeatType.NO }
        const checkArray = (() => {
          if (!listValueArray) return value
          // convert to a normal json structure for easier inspection
          return Value.toJson(value as Value) as AnyObject[]
        })()
        if (Array.isArray(checkArray)) {
          const matches = ARR_ITEM_SELECT_REGEX.exec(key)
          if (matches) {
            const [, k, v] = matches
            if (!k || k === 'undefined' || !v || v === 'undefined') {
              return null
            }
            let index = checkArray.findIndex((item) => String(item[k]) === v)
            if (index === -1) {
              if (!create || !meta.createMissingSelectors) return null
              if (listValueArray) {
                index = listValueArray.push(Value.fromJson({ [k]: v })) - 1
              } else {
                const selectorItem = createSelectorItem(message, k, v)
                if (!selectorItem) return null
                index = checkArray.push(selectorItem) - 1
              }
            }
            diffKey = key = String(index)
          }
          const item = checkArray[key as unknown as number]
          if (isObject(item) && 'uuid4' in item && item.uuid4) {
            diffKey = item.uuid4
          }
        }
      }
    } else {
      // look for selected quote
      if (
        !Array.isArray(value) &&
        value.quotes?.selected &&
        Array.isArray(value.quotes.selected) &&
        value.quotes.selected.length
      ) {
        meta.selectedQuote = `quotes.quotes.(uuid4=${value.quotes.selected[0]})`
      }
      // check for `(selectedQuote)` syntax
      if (key === '(selectedQuote)') {
        if (!meta.selectedQuote) {
          devtools.configWarning({
            type: 'data',
            title: 'Used a (selectedQuote) path, but no quote has been selected.',
          })
          return null
        }
        key = diffKey = meta.selectedQuote.split('.')[0]!
        rest = meta.selectedQuote.split('.').slice(1).concat(rest)
      }
      if (key === SCRATCH_KEY) {
        nextFieldInfo = StructFieldInfo
        // everything nested within scratch data is untyped so can be created freely
        meta.createMissingSelectors = true
      } else {
        switch (message.typeName) {
          case Struct.typeName:
          case Value.typeName:
            nextFieldInfo = ValueFieldInfo
            break
          case ApplicationFormResponse.typeName: {
            if (key === WORKING_COPIES_KEY) {
              nextFieldInfo = normalizeFieldInfo({
                no: 1,
                name: ApplicationFormResponse.typeName,
                kind: 'map',
                V: {
                  kind: 'message',
                  T: () => ApplicationFormResponse,
                },
                K: ScalarType.STRING,
              })
            } else if ((value as ApplicationFormResponse).value.oneofKind?.startsWith('overridable')) {
              const oneofKindEntry = getMessageFieldIndex(message).get((value as ApplicationFormResponse).value.oneofKind!)
              if (oneofKindEntry?.kind === 'local' && oneofKindEntry.field.kind === 'message') {
                const entry = getMessageFieldIndex(oneofKindEntry.field.T()).get(key)
                if (entry) {
                  nextFieldInfo = entry.kind === 'local' ? entry.field : fieldInfo
                }
              }
            } else {
              nextFieldInfo = ApplicationFormResponseFieldInfo
            }
            break
          }
          default: {
            // oneof fields are not nested in protobuf shapes so we drill one more time
            // (using the parent fieldInfo) to "unwrap" the oneof
            const entry = getMessageFieldIndex(message).get(key)
            if (entry) nextFieldInfo = entry.kind === 'local' ? entry.field : fieldInfo
          }
        }
      }
    }
  }
  if (!nextFieldInfo) return null

  const fullPath = paths.resolved ? `${paths.resolved}.${key}` : key
  const fullDiffPath = paths.diff ? `${paths.diff}.${diffKey}` : diffKey
  if (!rest.length) {
    return {
      value,
      key,
      fieldInfo: nextFieldInfo,
      parentTypeName: message?.typeName ?? '',
      paths: { resolved: fullPath, diff: fullDiffPath },
      meta,
    }
  }

  switch (nextFieldInfo.kind) {
    case 'message':
    case 'map':
      break
    default:
      // non-message array check
      if (isArrayFieldInfo(nextFieldInfo) && rest.length === 1) {
        let nextValue: AnyObject | AnyObject[] | null | undefined = value[key as keyof typeof value]
        if (!nextValue) {
          if (create) nextValue = assignCreated(value as Record<string, AnyObject[]>, key, [])
          else return null
        }
        key = rest[0]!
        return {
          value: nextValue,
          key,
          fieldInfo: { ...nextFieldInfo, repeat: RepeatType.NO },
          parentTypeName: '',
          paths: { resolved: `${fullPath}.${key}`, diff: `${fullDiffPath}.${key}` },
          meta,
        }
      }

      return null
  }

  let nextValue: AnyObject | AnyObject[] | null | undefined = null
  const nextKeyIsArrIndex = rest[0] === WORKING_COPIES_KEY || /^\d+$/.test(rest[0]!) || ARR_ITEM_SELECT_REGEX.test(rest[0]!)
  const debugPath = [fullPath, ...rest].join('.')
  switch (message?.typeName) {
    case Struct.typeName: {
      const val = value as Struct
      let v: Value | null = val.fields[key]!
      if (isMismatchedArray(v, nextKeyIsArrIndex, debugPath)) v = null
      if (!v && create) {
        v = assignCreated(val.fields, key, Value.fromJson(nextKeyIsArrIndex ? [] : {}))
      }
      nextValue = v
      break
    }
    case Value.typeName: {
      const val = value as Value
      switch (val.kind.oneofKind) {
        case 'structValue': {
          let v: Value | null = val.kind.structValue.fields[key]!
          if (isMismatchedArray(v, nextKeyIsArrIndex, debugPath)) v = null
          if (!v && create) {
            v = assignCreated(val.kind.structValue.fields, key, Value.fromJson(nextKeyIsArrIndex ? [] : {}))
          }
          nextValue = v
          break
        }
        case 'listValue': {
          let v: Value | null = val.kind.listValue.values[key as unknown as number]!
          if (isMismatchedArray(v, nextKeyIsArrIndex, debugPath)) v = null
          if (!v && create) {
            const prevLen = val.kind.listValue.values.length
            v = assignCreated(val.kind.listValue.values, key as unknown as number, Value.fromJson(nextKeyIsArrIndex ? [] : {}))
            fixSparseValueArray(val, prevLen)
          }
          nextValue = v
        }
      }
      break
    }
    case ApplicationFormResponse.typeName: {
      const val = value as ApplicationFormResponse
      switch (val.value.oneofKind) {
        case 'object':
          nextValue = val.value.object.fields[key]
          break
        case 'list': {
          let v: ApplicationFormResponse | Record<string, ApplicationFormResponse> | null = val.value.list.items[key as unknown as number] ?? null
          if (!v && create) {
            if (key === WORKING_COPIES_KEY) {
              v = assignCreated(val.value.list.items, key, {})!
            } else if (val.value.list[FORM_LIST_DEFAULT_ITEM_KEY]) {
              v = assignCreated(val.value.list.items, key as unknown as number, ApplicationFormResponse.clone(val.value.list[FORM_LIST_DEFAULT_ITEM_KEY]))
            }
          }
          if (key === WORKING_COPIES_KEY && val.value.list[FORM_LIST_DEFAULT_ITEM_KEY]) {
            meta.defaultDataItem = val.value.list[FORM_LIST_DEFAULT_ITEM_KEY]
          }
          nextValue = v
          break
        }
        case 'overridableBool':
        case 'overridableDate':
        case 'overridableDecimal':
        case 'overridableNumber':
        case 'overridableString':
        case 'overridableYesNoUnknown':
          nextValue = getOneofKindValue(val.value)
      }
      break
    }
    default: {
      let v = value[key as keyof typeof value]
      if (!v && create) {
        v = nextFieldInfo.kind === 'map'
          ? {}
          : isArrayFieldInfo(nextFieldInfo)
            ? []
            : meta.defaultDataItem
              ? ApplicationFormResponse.clone(meta.defaultDataItem)
              : nextFieldInfo.T().create()
        v = assignCreated(value, key as keyof typeof value, v)
        if (nextFieldInfo.oneof) {
          const oneofGroup = value as UnknownOneofGroup
          if (oneofGroup.oneofKind && oneofGroup.oneofKind !== key) {
            // clear the previous oneof value
            delete oneofGroup[oneofGroup.oneofKind]
            devtools.configWarning({
              type: 'data',
              title: `Overwriting existing oneof "${oneofGroup.oneofKind}" with "${key}" at drilled path "${paths.resolved}"`,
            })
          }
          if (oneofGroup.oneofKind !== key) oneofGroup.oneofKind = key
        }
      }
      nextValue = v
    }
  }
  if (!nextValue) return null
  return drillFieldInfo(create, nextFieldInfo, rest.join('.'), nextValue, { resolved: fullPath, diff: fullDiffPath }, meta)
}

/**
 * Converts an {@link ApplicationFormResponse} into the plain value it represents, unwrapping nested objects and lists recursively.
 *
 * @example
 * unwrapApplicationFormResponse({ value: { oneofKind: 'list', list: { items: [{ value: { oneofKind: 'string', string: 'TX' } }] } } }) // ['TX']
 */
export function unwrapApplicationFormResponse (response: ApplicationFormResponse): unknown {
  const v = response.value
  switch (v.oneofKind) {
    case 'date': return dateToString(v.date)
    case 'decimal': return decimalToString(v.decimal)
    case 'bool': return v.bool
    case 'number': {
      const num = +v.number
      return isNaN(num) ? 0 : num
    }
    case 'string': return v.string
    case 'yesNoUnknown': return v.yesNoUnknown
    case 'object':
      return Object.fromEntries(Object.entries(v.object.fields).map(([k, field]) => [k, unwrapApplicationFormResponse(field)]))
    case 'list': return v.list.items.map(unwrapApplicationFormResponse)
    case 'overridableBool': return OverridableBool.clone(v.overridableBool)
    case 'overridableDate': return OverridableDate.clone(v.overridableDate)
    case 'overridableDecimal': return OverridableDecimal.clone(v.overridableDecimal)
    case 'overridableNumber': return OverridableNumber.clone(v.overridableNumber)
    case 'overridableString': return OverridableString.clone(v.overridableString)
    case 'overridableYesNoUnknown': return OverridableYesNoUnknown.clone(v.overridableYesNoUnknown)
  }
}

/**
 * Takes a {@link DrilledFieldInfo} and returns the value to be read from it.
 *
 * @example
 * const info = drillFieldInfo(false, PolicyStateFieldInfo, 'effective.year', policyState)
 * const value = readFieldInfo<number>(info)
 */
export function readFieldInfo<T> (info: DrilledFieldInfo | null): T | undefined {
  if (!info) return
  const path = info.paths.resolved
  let readValue
  switch (info.parentTypeName) {
    case Value.typeName: {
      const val = info.value as Value
      switch (val.kind.oneofKind) {
        case 'structValue':
          if (!(info.key in val.kind.structValue.fields)) return undefined
          readValue = val.kind.structValue.fields[info.key]
          break
        case 'listValue': {
          const numericKey = Number(info.key)
          if (isNaN(numericKey)) return undefined
          if (!(numericKey in val.kind.listValue.values)) return undefined
          readValue = val.kind.listValue.values[numericKey]
          break
        }
        default:
          devtools.configWarning({
            type: 'input',
            title: `Could not read Value at ${path}`,
            description: 'Trying to access nested value but not a struct or list',
          })
          return undefined
      }
      break
    }
    case Struct.typeName: {
      const val = info.value as Struct
      if (!(info.key in val.fields)) return undefined
      readValue = val.fields[info.key]
      break
    }
    case ApplicationFormResponse.typeName: {
      const val = info.value as ApplicationFormResponse
      switch (val.value.oneofKind) {
        case 'object':
          if (!(info.key in val.value.object.fields)) return undefined
          readValue = val.value.object.fields[info.key]
          break
        case 'list': {
          const numericKey = Number(info.key)
          if (isNaN(numericKey)) return undefined
          if (!(numericKey in val.value.list.items)) return undefined
          readValue = val.value.list.items[numericKey]
          break
        }
        case 'overridableBool':
        case 'overridableDate':
        case 'overridableDecimal':
        case 'overridableNumber':
        case 'overridableString':
        case 'overridableYesNoUnknown': {
          const v = getOneofKindValue(val.value)
          if (!(info.key in v)) return undefined
          readValue = v[info.key as keyof typeof v]
          break
        }
        default:
          devtools.configWarning({
            type: 'input',
            title: `Could not read ApplicationFormResponse at ${path}`,
            description: 'Trying to access nested value but not an object, list or overridable',
          })
          return undefined
      }
      break
    }
    default:
      if (!(info.key in info.value)) return undefined
      readValue = info.value[info.key as keyof typeof info.value]
  }
  switch (info.fieldInfo.kind) {
    case 'message': {
      const message = info.fieldInfo.T()
      switch (message.typeName) {
        case DateStruct.typeName:
        case DateScheduleStruct.typeName:
          return dateToString(readValue as DateStruct) as T
        case Money.typeName: return moneyToString(readValue as Money) as T
        case Decimal.typeName: return decimalToString(readValue as Decimal) as T
        case Timestamp.typeName: return timestampToString(readValue as Timestamp) as T
        case Agency.typeName:
        case CoverHolder.typeName:
          return agencyOrCoverHolderToString(readValue as Agency | CoverHolder) as T
        case Struct.typeName: return Struct.toJson(readValue as Struct) as T
        case Value.typeName: return Value.toJson(readValue as Value) as T
        case ApplicationFormResponse.typeName:
          return unwrapApplicationFormResponse(readValue as ApplicationFormResponse) as T
      }
      break
    }
    case 'scalar':
      switch (info.fieldInfo.T) {
        case ScalarType.INT64: {
          const num = +readValue
          return (isNaN(num) ? 0 : num) as T
        }
      }
  }
  return readValue
}

/**
 * Writes a value into an existing {@link ApplicationFormResponse}, converting it to match the response's `oneofKind`.
 *
 * Objects and lists accept plain values, the same shape returned by {@link unwrapApplicationFormResponse}.
 * Objects have each key written into its matching existing field, creating any missing fields from `template`.
 * Lists are overwritten entirely, with each value written into a clone of the list's default item.
 *
 * @param template A response with the expected structure, such as a list's default item, used to create missing fields.
 */
function writeApplicationFormResponse (
  response: ApplicationFormResponse,
  value: unknown,
  path: string,
  template: ApplicationFormResponse | null = null,
): void {
  switch (response.value.oneofKind) {
    case 'date': response.value.date = stringToDate(value as string | null)!; break
    case 'decimal': response.value.decimal = stringToDecimal(value as string | null)!; break
    case 'bool': response.value.bool = value as boolean; break
    case 'number': response.value.number = (value && String(value)) as string; break
    case 'string': response.value.string = value as string; break
    case 'yesNoUnknown':
      response.value.yesNoUnknown = typeof value === 'boolean' ? booleanToYesNoUnknown(value) : value as number
      break
    case 'object':
      if (!isObject(value)) {
        devtools.configWarning({
          type: 'input',
          title: `Could not set object at ${path}`,
          description: `Value ${value} is not an object`,
        })
        return
      }
      writeApplicationFormFields(
        response.value.object.fields,
        value,
        path,
        template?.value.oneofKind === 'object' ? template.value.object.fields : {},
      )
      break
    case 'list': {
      const list = response.value.list
      const defaultItem = list[FORM_LIST_DEFAULT_ITEM_KEY]
      const values = value ?? []
      if (!Array.isArray(values) || !defaultItem) {
        devtools.configWarning({
          type: 'input',
          title: `Could not set list at ${path}`,
          description: !defaultItem ? 'List has no default item to create values from' : `Value ${value} is not an array`,
        })
        return
      }
      list.items = values.map((item) => {
        const itemResponse = ApplicationFormResponse.clone(defaultItem)
        writeApplicationFormResponse(itemResponse, item, path)
        return itemResponse
      })
      break
    }
    case 'overridableBool':
    case 'overridableDate':
    case 'overridableDecimal':
    case 'overridableNumber':
    case 'overridableString':
    case 'overridableYesNoUnknown':
      if (!isObject(value)) {
        devtools.configWarning({
          type: 'input',
          title: `Could not set overridable at ${path}`,
          description: `Value ${value} is not an object`,
        })
        return
      }
      switch (response.value.oneofKind) {
        case 'overridableBool': response.value.overridableBool = OverridableBool.create(value); break
        case 'overridableDate': response.value.overridableDate = OverridableDate.create(value); break
        case 'overridableDecimal': response.value.overridableDecimal = OverridableDecimal.create(value); break
        case 'overridableNumber': response.value.overridableNumber = OverridableNumber.create(value); break
        case 'overridableString': response.value.overridableString = OverridableString.create(value); break
        case 'overridableYesNoUnknown': response.value.overridableYesNoUnknown = OverridableYesNoUnknown.create(value); break
      }
  }
}

/**
 * Writes each key of a plain object into the matching existing field of an object {@link ApplicationFormResponse}.
 * Missing fields are created from `templateFields`, otherwise they are skipped as there is no way to know which response type they should be.
 */
function writeApplicationFormFields (
  fields: Record<string, ApplicationFormResponse>,
  value: AnyObject,
  path: string,
  templateFields: Record<string, ApplicationFormResponse>,
): void {
  for (const [k, v] of Object.entries(value)) {
    const fieldPath = `${path}.${k}`
    const templateField = templateFields[k] ?? null
    let field = fields[k]
    if (!field && templateField) {
      field = fields[k] = ApplicationFormResponse.clone(templateField)
    }
    if (!field) {
      devtools.configWarning({
        type: 'input',
        title: `Could not set Value at ${fieldPath}`,
        description: 'Field does not exist in the data structure',
      })
      continue
    }
    writeApplicationFormResponse(field, v, fieldPath, templateField)
  }
}

interface WriteTarget {
  /**
   * The object or array that directly contains the value being written.
   */
  targetValue: TSFixMe
  /**
   * The key or index of the value within `targetValue`.
   */
  key: string | number
  defaultDataItem: ApplicationFormResponse | null
  /**
   * The length of a `ListValue` before writing, used to fill any gaps left by the write.
   */
  fixSparseLength: number | null
}

/**
 * Resolves the container and key that a {@link DrilledFieldInfo} writes into, unwrapping parent oneof structures.
 * Returns `null` if the parent cannot be written into.
 */
function resolveWriteTarget (info: DrilledFieldInfo): WriteTarget | null {
  const path = info.paths.resolved
  const target: WriteTarget = {
    targetValue: info.value,
    key: info.key,
    defaultDataItem: info.meta.defaultDataItem,
    fixSparseLength: null,
  }
  switch (info.parentTypeName) {
    case ApplicationFormResponse.typeName: {
      const infoValue = info.value as ApplicationFormResponse
      switch (infoValue.value.oneofKind) {
        case 'object':
          target.targetValue = infoValue.value.object.fields
          break
        case 'list':
          target.targetValue = infoValue.value.list.items
          target.key = Number(info.key)
          target.defaultDataItem = infoValue.value.list[FORM_LIST_DEFAULT_ITEM_KEY] ?? null
          break
        case 'overridableBool':
        case 'overridableDate':
        case 'overridableDecimal':
        case 'overridableNumber':
        case 'overridableString':
        case 'overridableYesNoUnknown':
          target.targetValue = getOneofKindValue(infoValue.value)
          break
        default:
          devtools.configWarning({
            type: 'input',
            title: `Could not set Value at ${path}`,
            description: 'Trying to set nested value but not an object, list or overridable',
          })
          return null
      }
      break
    }
    case Struct.typeName: {
      target.targetValue = (info.value as Struct).fields
      break
    }
    case Value.typeName: {
      const infoValue = info.value as Value
      switch (infoValue?.kind?.oneofKind) {
        case 'structValue':
          target.targetValue = infoValue.kind.structValue.fields
          break
        case 'listValue': {
          target.key = Number(info.key)
          target.fixSparseLength = infoValue.kind.listValue.values.length
          target.targetValue = infoValue.kind.listValue.values
          break
        }
        default:
          devtools.configWarning({
            type: 'input',
            title: `Could not set Value at ${path}`,
            description: 'Trying to set nested value but not a struct or list',
          })
      }
    }
  }
  return target
}

/**
 * Returns the stored array for a {@link DrilledFieldInfo}, either a repeated protobuf field or the items of an {@link ApplicationFormResponse} list.
 * The array is returned as stored so it can be mutated directly, unlike {@link readFieldInfo} which returns plain copies of responses.
 * Returns `null` if there is no array at the path.
 */
export function getWritableArray (info: DrilledFieldInfo): unknown[] | null {
  const target = resolveWriteTarget(info)
  if (!target) return null
  const value = target.targetValue[target.key]
  if (Array.isArray(value)) return value
  const response = value as ApplicationFormResponse | undefined
  if (response?.value?.oneofKind === 'list') return response.value.list.items
  return null
}

/**
 * Takes a {@link DrilledFieldInfo} and writes the specified value to it.
 *
 * @example
 * const info = drillFieldInfo(true, PolicyStateFieldInfo, 'effective.year', policyState)
 * if (!info) {
 *   // handle a bad path here
 *   return
 * }
 * writeFieldInfo(info, 2025)
 */
export function writeFieldInfo (info: DrilledFieldInfo, value: unknown): void {
  const path = info.paths.resolved
  const target = resolveWriteTarget(info)
  if (!target) return
  const { targetValue, key, defaultDataItem, fixSparseLength } = target

  switch (info.fieldInfo.kind) {
    case 'message': {
      const message = info.fieldInfo.T()

      if (key === WORKING_COPIES_KEY) {
        // overwrite entire working copies
        targetValue[key] = value
        return
      }

      if (isArrayFieldInfo(info.fieldInfo)) {
        if (!Array.isArray(value)) {
          devtools.configWarning({
            type: 'input',
            title: `Failed to set array path ${path} to a non-array value ${value}`,
          })
        } else {
          // overwrites entire array with value
          targetValue[key as keyof typeof targetValue] = value
        }
        return
      }

      let setValue: AnyObject | null = null
      switch (message.typeName) {
        case DateStruct.typeName:
        case DateScheduleStruct.typeName:
          setValue = stringToDate(value as string | null); break
        case Money.typeName: setValue = stringToMoney(value as string | null); break
        case Decimal.typeName: setValue = stringToDecimal(value as string | null); break
        case Timestamp.typeName: setValue = stringToTimestamp(value as string | null); break
        case Agency.typeName:
        case CoverHolder.typeName: {
          setValue = stringToAgencyOrCoverHolder(value as string | null)
          break
        }
        case Struct.typeName:
          setValue = Struct.fromJson(value as AnyObject)
          break
        case Value.typeName: {
          setValue = Value.fromJson((value ?? null) as JsonValue)
          break
        }
        case ApplicationFormResponse.typeName: {
          let v: ApplicationFormResponse | null = null
          v = targetValue[key as keyof typeof targetValue]
          if (!v && defaultDataItem) {
            v = targetValue[key as keyof typeof targetValue] = ApplicationFormResponse.clone(defaultDataItem)
          }
          if (!v) {
            devtools.configWarning({
              type: 'input',
              title: `Could not set Value at ${path}`,
              description: 'Unknown data structure',
              context: JSON.stringify(targetValue, null, 2),
            })
            return
          }
          writeApplicationFormResponse(v, value, path, defaultDataItem)
          return
        }
        default:
          if (isObject(value)) {
            if (message.fields.some((f) => f.oneof === key)) {
              // this is setting a oneofGroup
              setValue = message.create({ [key]: value })[key]
            } else if (info.fieldInfo.oneof) {
              // this is setting a oneof so make sure the oneofGroup has the right kind
              const oneofGroup = targetValue as UnknownOneofGroup
              if (oneofGroup.oneofKind && oneofGroup.oneofKind !== key) {
                // clear the previous oneof value
                delete oneofGroup[oneofGroup.oneofKind]
                devtools.configWarning({
                  type: 'data',
                  title: `Overwriting existing oneof "${oneofGroup.oneofKind}" with "${key}" at set path "${info.paths.resolved}"`,
                })
              }
              if (oneofGroup.oneofKind !== key) oneofGroup.oneofKind = key as string
              setValue = message.create(value as AnyObject)
            } else {
              setValue = message.create(value as AnyObject)
            }
          } else if (value !== null) {
            devtools.configWarning({
              type: 'input',
              title: `Failed to set object path ${path} to a non-object value ${value}`,
            })
            return
          }
      }

      const messageKey = key as keyof typeof targetValue
      const previousMessage = targetValue[messageKey]
      if (previousMessage && typeof previousMessage === 'object' && setValue && typeof setValue === 'object') {
        message.mergePartial(previousMessage, setValue)
      } else {
        targetValue[messageKey] = setValue
      }
      if (typeof fixSparseLength === 'number') fixSparseValueArray(info.value as Value, fixSparseLength)
      return
    }
    case 'scalar': {
      const scalarType = info.fieldInfo.T
      if (isArrayFieldInfo(info.fieldInfo)) {
        if (!Array.isArray(value)) {
          devtools.configWarning({
            type: 'input',
            title: `Failed to set array path ${path} to a non-array value ${value}`,
          })
        } else {
          // overwrites entire array with value
          targetValue[key as keyof typeof targetValue] = value.map((v) => convertScalar(v, scalarType))
        }
        return
      }
      targetValue[key as keyof typeof targetValue] = convertScalar(value, scalarType)
      return
    }
    case 'enum': {
      // get enum type name
      const name = info.fieldInfo.T()[0]

      switch (name) {
        case 'policyfly.YesNoUnknown':
          value = typeof value === 'boolean' ? booleanToYesNoUnknown(value) : value
      }
      targetValue[key as keyof typeof targetValue] = value
      return
    }
    default:
      targetValue[key as keyof typeof targetValue] = value
  }
}
